// Human-readable copy for the workflow states' `on_enter` side-effects.
//
// Each state in the ESS workflow yaml carries an `on_enter` list of
// notification callbacks the backend fires when a suggestion *enters*
// that state. The backend owns the display copy: the workflow config
// payload ships a `callback_labels` map (hook name -> label, see
// `near-miss-backend` `to_public_safe_workflow`). This module just looks
// a hook up in that map, and humanises the raw name as a fallback for any
// hook the backend hasn't labelled yet — so the canvas never shows a bare
// `send_ess_*_alert` identifier.

// Domain acronyms preserved verbatim when humanising an unmapped hook.
const TRIGGER_ACRONYMS = new Set(["ESS", "HR", "WC", "SC", "HOD", "NM"]);

// Fallback for any hook the backend's `callback_labels` map doesn't cover.
// Strips the generic `send_` / `ess_` prefixes and the `_alert(s)` suffix,
// then Title-Cases the remainder into "Sends the '…' notification".
export function humanizeTriggerName(raw: string): string {
  const core = raw
    .replace(/^send_/, "")
    .replace(/^ess_/, "")
    .replace(/_alerts?$/, "");
  const words = core
    .split("_")
    .filter(Boolean)
    .map((w) => {
      const upper = w.toUpperCase();
      if (TRIGGER_ACRONYMS.has(upper)) return upper;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
  if (!words) return "Runs an automatic step";
  return `Sends the '${words}' notification`;
}

// Backend-provided label when present, humanised fallback otherwise.
export function triggerLabel(
  raw: string,
  labels?: Record<string, string>,
): string {
  return labels?.[raw] ?? humanizeTriggerName(raw);
}
